import React, { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import MergeIcon from '@mui/icons-material/Merge';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import type {
  ProductsListItem,
  QuoteItem,
  QuoteOption,
  QuoteType,
} from '@techgitter/contracts';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { QuoteOptionSearchDialog } from './QuoteOptionSearchDialog';
import { DistiListingSearchDialog } from './DistiListingSearchDialog';
import { AddRetailOptionDialog } from './AddRetailOptionDialog';
import { InventoryPickerDialog } from './InventoryPickerDialog';
import { PriceInput } from './PriceInput';
import { BillingGroupSelect, BillingGroupsPanel } from './BillingGroupControls';
import {
  formatSpecSummary,
  specDiffersFromDescription,
} from '../../utils/quoteOptionSpecs';
import {
  mergeItems,
  extractOptionToItem,
} from '../../utils/quoteItemOperations';
import { cleanSupplierUrl } from '../../utils/cleanSupplierUrl';
import { optionQty, optionLineSellDisplay } from '../../utils/quotePricing';

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const blankOption = (): QuoteOption => ({
  id: newId(),
  description: '',
  buyPriceExGst: 0,
  shipping: 0,
  url: '',
  sellPrice: 0,
  productCode: '',
  quantity: 1,
});

const blankItem = (position: number): QuoteItem => ({
  id: newId(),
  name: '',
  position,
  selectedOptionId: null,
  markedForInvoice: false,
  options: [blankOption()],
});

interface QuoteEditorProps {
  items: QuoteItem[];
  onChange: (items: QuoteItem[]) => void;
  disabled?: boolean;
  /** Quote type — drives the inventory picker's default category scope. */
  quoteType?: QuoteType;
  /** Quote-level GST display mode. Forwarded to the supplier picker so its
   *  per-row sell-price column reflects the same mode the customer will see. */
  priceDisplayMode?: 'ex' | 'inc';
  /** Quote-level markup applied by the supplier picker. */
  markup?: number;
}

export const QuoteEditor: React.FC<QuoteEditorProps> = ({
  items,
  onChange,
  disabled,
  quoteType = 'new_laptop',
  priceDisplayMode = 'ex',
  markup = 0.115,
}) => {
  // Storage stays in ex-GST end-to-end (matches QuoteOption contract);
  // each PriceInput handles the inc/ex conversion locally.
  const gstSuffix = priceDisplayMode === 'inc' ? 'inc-GST' : 'ex-GST';
  const [inventoryPickerOpen, setInventoryPickerOpen] = useState(false);
  const [searchTarget, setSearchTarget] = useState<string | null>(null);
  // When non-null, the supplier picker is open and will write its result
  // back as a NEW option appended to the targeted item-group.
  const [supplierTargetItemId, setSupplierTargetItemId] = useState<
    string | null
  >(null);
  // When non-null, the "Add retail option" dialog is open and will
  // append a new QuoteOption (sourced from a retailer URL paste) to
  // the targeted item-group.
  const [retailTargetItemId, setRetailTargetItemId] = useState<
    string | null
  >(null);
  // Item ids ticked for grouping. Stored as a click-ordered array so the
  // first selected item is the survivor when "Group selected" is invoked.
  const [selected, setSelected] = useState<string[]>([]);
  const toggleSelected = useCallback((id: string) => {
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  }, []);

  const updateItem = useCallback(
    (id: string, patch: Partial<QuoteItem>) => {
      onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    },
    [items, onChange],
  );

  const updateOption = useCallback(
    (itemId: string, optionId: string, patch: Partial<QuoteOption>) => {
      onChange(
        items.map((it) =>
          it.id === itemId
            ? {
                ...it,
                options: it.options.map((o) =>
                  o.id === optionId ? { ...o, ...patch } : o,
                ),
              }
            : it,
        ),
      );
    },
    [items, onChange],
  );

  const addItem = useCallback(() => {
    onChange([...items, blankItem(items.length)]);
  }, [items, onChange]);

  const addItemFromInventory = useCallback(
    (product: ProductsListItem) => {
      const optionId = newId();
      // ``unitCost`` on the catalog row is ``cr001_price_retail`` —
      // GST-inclusive in AU. ``buyPriceExGst`` / ``sellPrice`` on
      // QuoteOption are ex-GST, so divide by 1.10 on the way in.
      // Assumption: all picker items are GST-taxable. The picker uses
      // ``ProductsListItem`` which doesn't carry the ``taxable`` flag
      // (``CatalogProduct`` does); revisit if non-taxable inventory
      // becomes a real case.
      const exGstCost =
        Math.round(((product.unitCost ?? 0) / 1.1) * 100) / 100;
      const option: QuoteOption = {
        id: optionId,
        description: product.displayName || product.sku,
        buyPriceExGst: exGstCost,
        shipping: 0,
        url: '',
        sellPrice: exGstCost,
        productCode: product.sku || '',
        quantity: 1,
      };
      const item: QuoteItem = {
        id: newId(),
        name: product.displayName || product.sku,
        position: items.length,
        selectedOptionId: optionId,
        markedForInvoice: true,
        options: [option],
      };
      onChange([...items, item]);
    },
    [items, onChange],
  );

  const removeItem = useCallback(
    (id: string) => {
      onChange(
        items
          .filter((it) => it.id !== id)
          .map((it, idx) => ({ ...it, position: idx })),
      );
      setSelected((s) => s.filter((x) => x !== id));
    },
    [items, onChange],
  );

  const moveOptionToOwnItem = useCallback(
    (itemId: string, optionId: string) => {
      onChange(extractOptionToItem(items, itemId, optionId));
    },
    [items, onChange],
  );

  const groupSelected = useCallback(() => {
    // `selected` is already in click order, which is the survivor-precedence
    // order: the first item ticked keeps its name and position.
    onChange(mergeItems(items, selected));
    setSelected([]);
  }, [items, onChange, selected]);

  const addOption = useCallback(
    (itemId: string) => {
      onChange(
        items.map((it) =>
          it.id === itemId
            ? { ...it, options: [...it.options, blankOption()] }
            : it,
        ),
      );
    },
    [items, onChange],
  );

  const removeOption = useCallback(
    (itemId: string, optionId: string) => {
      onChange(
        items.map((it) => {
          if (it.id !== itemId) return it;
          const remaining = it.options.filter((o) => o.id !== optionId);
          return {
            ...it,
            options: remaining,
            selectedOptionId:
              it.selectedOptionId === optionId ? null : it.selectedOptionId,
          };
        }),
      );
    },
    [items, onChange],
  );

  // Repair Service hint: a typical repair quote includes both a
  // labour line and a part. We sniff the selected options' product
  // codes for the catalog category prefix to detect what's there.
  // Detection is conservative — when the tech picks from inventory
  // we know the category; for hand-typed options we miss labour
  // entries unless they carry the SKU.
  const repairCoverage = useMemo(() => {
    if (quoteType !== 'repair') return null;
    let hasLabour = false;
    let hasPart = false;
    for (const it of items) {
      const opt = it.options.find((o) => o.id === it.selectedOptionId)
        ?? it.options[0];
      if (!opt) continue;
      const code = (opt.productCode || '').toLowerCase();
      if (code.includes('labour') || code.startsWith('rcs.') || code.startsWith('support-labour')) {
        hasLabour = true;
      } else if (code) {
        hasPart = true;
      }
    }
    if (hasLabour && hasPart) return null;
    if (!hasLabour && !hasPart) {
      return 'Repair Service quotes typically include a labour line and a part. Use Add from Inventory to start.';
    }
    if (!hasLabour) return 'No labour line detected. Most repair quotes include a labour charge — Add from Inventory and pick a Labour SKU.';
    return 'No part line detected. Most repair quotes include a hardware part — Add from Inventory and pick a Hardware SKU.';
  }, [quoteType, items]);

  return (
    <Box>
      {repairCoverage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {repairCoverage}
        </Alert>
      )}
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No items yet. Add an item to start quoting.
        </Typography>
      )}

      {items.map((item) => {
        const isSingle = item.options.length === 1;
        const isSelected = selected.includes(item.id);

        const selectionCheckbox = (
          <Tooltip title="Select for grouping">
            <Checkbox
              checked={isSelected}
              size="small"
              onChange={() => toggleSelected(item.id)}
              onClick={(e) => e.stopPropagation()}
              disabled={disabled}
            />
          </Tooltip>
        );

        const markCheckbox = (
          <FormControlLabel
            control={
              <Checkbox
                checked={item.markedForInvoice}
                onChange={(e) =>
                  updateItem(item.id, {
                    markedForInvoice: e.target.checked,
                  })
                }
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label="Mark"
            onClick={(e) => e.stopPropagation()}
          />
        );

        const nameField = (
          <TextField
            size="small"
            placeholder="Item name (e.g. Monitor)"
            value={item.name}
            onChange={(e) => updateItem(item.id, { name: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
        );

        const removeItemButton = (
          <Tooltip title="Remove item">
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(item.id);
                }}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );

        const renderOptionFields = (opt: QuoteOption) => (
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField
                label="Description"
                value={opt.description}
                size="small"
                onChange={(e) =>
                  updateOption(item.id, opt.id, {
                    description: e.target.value,
                  })
                }
                disabled={disabled}
                fullWidth
              />
              <TextField
                label="Product code"
                value={opt.productCode}
                size="small"
                onChange={(e) =>
                  updateOption(item.id, opt.id, {
                    productCode: e.target.value,
                  })
                }
                disabled={disabled}
                sx={{ width: 180 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <PriceInput
                label={`Buy ${gstSuffix}`}
                exValue={opt.buyPriceExGst}
                priceDisplayMode={priceDisplayMode}
                onChange={(exVal) =>
                  updateOption(item.id, opt.id, { buyPriceExGst: exVal })
                }
                disabled={disabled}
                sx={{ width: 140 }}
              />
              <PriceInput
                label={`Shipping ${gstSuffix}`}
                exValue={opt.shipping}
                priceDisplayMode={priceDisplayMode}
                onChange={(exVal) =>
                  updateOption(item.id, opt.id, { shipping: exVal })
                }
                disabled={disabled}
                sx={{ width: 140 }}
              />
              <TextField
                label="Qty"
                type="text"
                inputMode="numeric"
                size="small"
                value={String(optionQty(opt))}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  // Empty / invalid falls back to 1 (a line is always at
                  // least one unit); whole units only.
                  const parsed = parseInt(v, 10);
                  updateOption(item.id, opt.id, {
                    quantity:
                      Number.isFinite(parsed) && parsed >= 1 ? parsed : 1,
                  });
                }}
                disabled={disabled}
                sx={{ width: 80 }}
              />
              <PriceInput
                label={`Sell/unit ${gstSuffix}`}
                exValue={opt.sellPrice}
                priceDisplayMode={priceDisplayMode}
                onChange={(exVal) =>
                  updateOption(item.id, opt.id, { sellPrice: exVal })
                }
                disabled={disabled}
                sx={{ width: 140 }}
              />
              <TextField
                label="Source URL"
                size="small"
                value={opt.url}
                onChange={(e) =>
                  updateOption(item.id, opt.id, { url: e.target.value })
                }
                onBlur={(e) => {
                  const cleaned = cleanSupplierUrl(e.target.value);
                  if (cleaned !== e.target.value) {
                    updateOption(item.id, opt.id, { url: cleaned });
                  }
                }}
                disabled={disabled}
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              label="Internal note"
              placeholder="Optional — not shown to the customer"
              value={opt.note ?? ''}
              size="small"
              onChange={(e) =>
                updateOption(item.id, opt.id, {
                  note: e.target.value,
                })
              }
              disabled={disabled}
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              sx={{ mb: 1 }}
            />
            {optionQty(opt) > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Line total {gstSuffix}: $
                {optionLineSellDisplay(opt, priceDisplayMode).toFixed(2)}{' '}
                ({optionQty(opt)} × {opt.sellPrice.toFixed(2)})
              </Typography>
            )}
            {opt.url && (
              <Link
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
              >
                Open supplier link
              </Link>
            )}
            {/*
              Structured spec summary, only shown when the option was
              sourced from a disti listing AND the spec line carries
              detail not already in the description (e.g. specific CPU
              model number, RAM type, storage interface).
            */}
            {specDiffersFromDescription(opt.sourceSpecs, opt.description) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Specs: {formatSpecSummary(opt.sourceSpecs)}
              </Typography>
            )}
          </Box>
        );

        const actionButtons = (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addOption(item.id)}
              disabled={disabled}
            >
              Add Option
            </Button>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setSearchTarget(item.id)}
              disabled={disabled}
            >
              Search Past Options
            </Button>
            <Button
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={() => setSupplierTargetItemId(item.id)}
              disabled={disabled}
            >
              Find Product
            </Button>
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setRetailTargetItemId(item.id)}
              disabled={disabled}
            >
              Add Retail
            </Button>
          </Stack>
        );

        if (isSingle) {
          // Flat card: no accordion, no radio. Adding a 2nd option (via
          // any of the action buttons below) auto-promotes this same item
          // to the accordion branch on the next render.
          const opt = item.options[0];
          return (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                mb: 1,
                p: 1.5,
                bgcolor: isSelected ? 'action.selected' : undefined,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                {selectionCheckbox}
                {markCheckbox}
                {nameField}
                {item.markedForInvoice && (
                  <BillingGroupSelect
                    item={item}
                    items={items}
                    onChange={onChange}
                    disabled={disabled}
                    sx={{ width: 170 }}
                  />
                )}
                {removeItemButton}
              </Stack>
              {renderOptionFields(opt)}
              {actionButtons}
            </Paper>
          );
        }

        return (
          <Accordion key={item.id} defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                width="100%"
              >
                {selectionCheckbox}
                {markCheckbox}
                {nameField}
                <Typography variant="caption" color="text.secondary">
                  {item.options.length} options
                </Typography>
                {item.markedForInvoice && (
                  <BillingGroupSelect
                    item={item}
                    items={items}
                    onChange={onChange}
                    disabled={disabled}
                    sx={{ width: 170 }}
                  />
                )}
                {removeItemButton}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <RadioGroup
                value={item.selectedOptionId ?? ''}
                onChange={(_, value) =>
                  updateItem(item.id, { selectedOptionId: value || null })
                }
              >
                <Stack spacing={1.5}>
                  {item.options.map((opt) => (
                    <Paper
                      key={opt.id}
                      variant="outlined"
                      sx={{ p: 1.5, bgcolor: 'background.default' }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <Radio value={opt.id} size="small" disabled={disabled} />
                        {renderOptionFields(opt)}
                        <Tooltip title="Move to its own item">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                moveOptionToOwnItem(item.id, opt.id)
                              }
                              disabled={disabled}
                            >
                              <CallSplitIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => removeOption(item.id, opt.id)}
                          disabled={disabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </RadioGroup>

              {actionButtons}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addItem}
          disabled={disabled}
        >
          Add Item
        </Button>
        <Button
          variant="outlined"
          startIcon={<InventoryIcon />}
          onClick={() => setInventoryPickerOpen(true)}
          disabled={disabled}
        >
          Add from Inventory
        </Button>
        {selected.length >= 2 && (
          <Tooltip title="Merge the selected items into one item whose options become alternatives the customer picks between (not a billing group)">
            <Button
              variant="contained"
              startIcon={<MergeIcon />}
              onClick={groupSelected}
              disabled={disabled}
            >
              Merge as options ({selected.length})
            </Button>
          </Tooltip>
        )}
      </Stack>

      <BillingGroupsPanel
        items={items}
        onChange={onChange}
        disabled={disabled}
        priceDisplayMode={priceDisplayMode}
      />

      <InventoryPickerDialog
        open={inventoryPickerOpen}
        onClose={() => setInventoryPickerOpen(false)}
        quoteType={quoteType}
        onPick={addItemFromInventory}
      />

      <QuoteOptionSearchDialog
        open={searchTarget !== null}
        onClose={() => setSearchTarget(null)}
        onPick={(result) => {
          if (!searchTarget) return;
          const cloned: QuoteOption = {
            id: newId(),
            description: result.description,
            buyPriceExGst: result.buyPriceExGst,
            shipping: result.shipping,
            url: result.url,
            sellPrice: result.sellPrice,
            productCode: result.productCode,
          };
          onChange(
            items.map((it) =>
              it.id === searchTarget
                ? { ...it, options: [...it.options, cloned] }
                : it,
            ),
          );
          setSearchTarget(null);
        }}
      />

      <DistiListingSearchDialog
        open={supplierTargetItemId !== null}
        onClose={() => setSupplierTargetItemId(null)}
        initialProductCode={
          // If the targeted item has options that already share a product
          // code, scope the picker to that code by default. The user can
          // clear it inside the dialog to widen the search.
          (() => {
            if (!supplierTargetItemId) return undefined;
            const target = items.find((it) => it.id === supplierTargetItemId);
            const codes = (target?.options ?? [])
              .map((o) => o.productCode)
              .filter(Boolean);
            return codes[0] || undefined;
          })()
        }
        priceDisplayMode={priceDisplayMode}
        markup={markup}
        onPick={(result) => {
          if (!supplierTargetItemId) return;
          const fromSupplier: QuoteOption = {
            id: newId(),
            description: result.description,
            buyPriceExGst: result.costExGst,
            shipping: result.shippingExGst,
            url: result.distiUrl,
            // sellPrice is the picker's markup-applied figure; the tech
            // can still override per-option in the editor row. POA rows
            // come through with sellPriceExGst=0 so the tech is forced
            // to confirm pricing before quoting.
            sellPrice: result.priceUnknown ? 0 : result.sellPriceExGst,
            productCode: result.productCode || '',
            imageUrl: result.imageUrl,
            // Back-pointer so the quote-detail view can later check
            // whether this option's source listing has gone stale,
            // changed price, been deactivated, or been removed.
            sourceDistiId: result.distiId,
            sourceDistiSku: result.distiSku,
            sourceSpecs: result.specs,
          };
          onChange(
            items.map((it) =>
              it.id === supplierTargetItemId
                ? {
                    ...it,
                    options: [...it.options, fromSupplier],
                    // Auto-select the new option when the item has no
                    // selection yet. Without this, the freshly-added
                    // option is invisible to the Mark-for-invoice and
                    // promotion paths, which silently skip items
                    // whose selectedOptionId is null.
                    selectedOptionId: it.selectedOptionId ?? fromSupplier.id,
                  }
                : it,
            ),
          );
          // Dialog stays open after a pick so the tech can add more
          // options from the same supplier search; it tracks added
          // rows internally and is dismissed via its "Back to quote"
          // button.
        }}
      />

      <AddRetailOptionDialog
        open={retailTargetItemId !== null}
        onClose={() => setRetailTargetItemId(null)}
        onPick={(preview, overrides, commit) => {
          if (!retailTargetItemId) return;
          const fromRetail: QuoteOption = {
            id: newId(),
            description: overrides.description,
            buyPriceExGst: overrides.buyPriceExGst,
            // Retail purchases don't carry separate freight in our
            // model — the retailer's price already includes any
            // delivery cost (or pickup is free). Tech overrides on
            // the row if a delivery surcharge applies.
            shipping: 0,
            url: preview.url,
            sellPrice: overrides.sellPriceExGst,
            // Product code derived from the picked device-type
            // catalog entry in the retail dialog (auto-classified
            // from specs when a rule matched, otherwise tech-selected).
            productCode: overrides.productCode,
            // Device type the tech picked from the admin-managed
            // catalog — same dropdown drives both fields, so this is
            // always populated alongside productCode.
            deviceType: overrides.deviceType,
            imageUrl: preview.imageUrl ?? null,
            // Carry the structured spec snapshot through so the
            // quote-detail page can render the spec line under the
            // option (same pattern as disti-sourced options).
            sourceSpecs: preview.specs,
            // Back-pointers to the persisted retailer listing
            // (auto-created vendor row + URL-path-derived dedupe
            // sku). Stamps the option as discoverable from the
            // disti picker on future quotes — same as disti-sourced
            // options.
            sourceDistiId: commit.distiId,
            sourceDistiSku: commit.distiSku,
          };
          onChange(
            items.map((it) =>
              it.id === retailTargetItemId
                ? {
                    ...it,
                    options: [...it.options, fromRetail],
                    // Auto-select the new option when the item has no
                    // selection yet — see the matching block in the
                    // supplier dialog onPick above. Without this, the
                    // Mark-for-invoice toggle appears non-functional
                    // because the silent `!selectedOptionId` filter
                    // excludes the item from totals and promotion.
                    selectedOptionId: it.selectedOptionId ?? fromRetail.id,
                  }
                : it,
            ),
          );
          setRetailTargetItemId(null);
        }}
      />
    </Box>
  );
};
